import React from 'react';
import { EmptyState, InfoCard, Progress } from '@backstage/core-components';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { LogViewer, StatusChip, measureAsync, trackUxSignal } from '../ui';

type BuildItem = {
  id?: string;
  status?: string;
  branchName?: string;
  shortSha?: string;
  startTime?: string;
  finishTime?: string;
  logUrl?: string;
};

type BuildListResponse = {
  builds?: BuildItem[];
  resolved?: {
    projectId?: string;
    location?: string;
    triggerName?: string;
  };
};

type BuildLogItem = {
  buildId?: string;
  timestamp?: string;
  severity?: string;
  buildStep?: string;
  textPayload?: string;
  message?: string;
  jsonPayload?: Record<string, unknown>;
};

type BuildLogsResponse = {
  logs?: BuildLogItem[];
  resolved?: {
    projectId?: string;
    location?: string;
    triggerName?: string;
    buildId?: string;
    limit?: number;
  };
};

const BUILD_LOG_DEFAULT_LIMIT = 1000;

type ViewState = {
  loadingBuilds: boolean;
  loadingBuildLogs: boolean;
  error: string;
  builds: BuildItem[];
  buildMeta: BuildListResponse['resolved'];
  selectedBuildId: string;
  buildLogs: BuildLogItem[];
  buildLogsMeta: BuildLogsResponse['resolved'];
  lastUpdatedAt: number;
};

const INITIAL_STATE: ViewState = {
  loadingBuilds: true,
  loadingBuildLogs: false,
  error: '',
  builds: [],
  buildMeta: {},
  selectedBuildId: '',
  buildLogs: [],
  buildLogsMeta: {},
  lastUpdatedAt: 0,
};

export type EntityPlatformLogsContentProps = {
  mode?: 'build';
};

function formatTimestamp(value?: string): string {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function extractLogMessage(log: BuildLogItem): string {
  if (typeof log.textPayload === 'string' && log.textPayload.trim())
    return log.textPayload;
  if (typeof log.message === 'string' && log.message.trim()) return log.message;
  if (log.jsonPayload) {
    try {
      return JSON.stringify(log.jsonPayload);
    } catch {
      return String(log.jsonPayload);
    }
  }
  return '';
}

async function parseResponsePayload(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractErrorMessage(payload: unknown): string {
  if (!payload) return 'Unknown API error';
  if (typeof payload === 'string') return payload;
  if (typeof payload !== 'object') return String(payload);
  const obj = payload as Record<string, unknown>;
  const direct = String(obj.message ?? '').trim();
  if (direct) return direct;
  const nested = obj.error as Record<string, unknown> | undefined;
  const nestedMessage = String(nested?.message ?? '').trim();
  if (nestedMessage) return nestedMessage;
  return 'Unknown API error';
}

async function fetchJson<T>(
  fetchApi: { fetch: typeof fetch },
  path: string,
): Promise<T> {
  const response = await fetchApi.fetch(path);
  const payload = await parseResponsePayload(response);
  if (!response.ok) {
    throw new Error(`${response.status} ${extractErrorMessage(payload)}`);
  }
  return (payload ?? {}) as T;
}

function appendError(existing: string, next: string): string {
  if (!next) return existing;
  if (!existing) return next;
  return `${existing}\n${next}`;
}

function relativeLastUpdated(value: number): string {
  if (!value) return 'Never';
  const deltaSec = Math.max(0, Math.floor((Date.now() - value) / 1000));
  if (deltaSec < 5) return 'just now';
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const deltaMin = Math.floor(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin}m ago`;
  const deltaHour = Math.floor(deltaMin / 60);
  return `${deltaHour}h ago`;
}

export const EntityPlatformLogsContent = (
  _props: EntityPlatformLogsContentProps,
) => {
  const fetchApi = useApi(fetchApiRef);
  const { entity } = useEntity();

  const componentId = String(entity?.metadata?.uid ?? '').trim();

  const [logsExpanded, setLogsExpanded] = React.useState(false);
  const [state, setState] = React.useState<ViewState>(INITIAL_STATE);
  const buildListCacheRef = React.useRef<Map<string, BuildListResponse>>(
    new Map(),
  );
  const buildLogsCacheRef = React.useRef<Map<string, BuildLogsResponse>>(
    new Map(),
  );

  const loadBuildList = React.useCallback(async () => {
    if (!componentId) return;

    const buildListCacheKey = `${componentId}:limit-5`;
    const cached = buildListCacheRef.current.get(buildListCacheKey);
    if (cached) {
      const builds = Array.isArray(cached?.builds) ? cached.builds : [];
      const selectedFromList = String(builds[0]?.id ?? '').trim();
      setState(prev => {
        const stillExists =
          prev.selectedBuildId &&
          builds.some(item => item.id === prev.selectedBuildId);
        const selectedBuildId = stillExists
          ? prev.selectedBuildId
          : selectedFromList;
        return {
          ...prev,
          builds,
          buildMeta: cached?.resolved ?? {},
          selectedBuildId,
        };
      });
    }

    setState(prev => ({ ...prev, loadingBuilds: true }));
    try {
      const payload = await measureAsync(
        'ui.buildList.fetch',
        async () => {
          const path = `/api/build-logs/builds/by-id/${encodeURIComponent(
            componentId,
          )}?limit=5`;
          return await fetchJson<BuildListResponse>(fetchApi, path);
        },
        { componentId },
      );
      const builds = Array.isArray(payload?.builds) ? payload.builds : [];
      const selectedFromList = String(builds[0]?.id ?? '').trim();
      buildListCacheRef.current.set(buildListCacheKey, payload);

      setState(prev => {
        const stillExists =
          prev.selectedBuildId &&
          builds.some(item => item.id === prev.selectedBuildId);
        const selectedBuildId = stillExists
          ? prev.selectedBuildId
          : selectedFromList;
        return {
          ...prev,
          loadingBuilds: false,
          builds,
          buildMeta: payload?.resolved ?? {},
          selectedBuildId,
          lastUpdatedAt: Date.now(),
        };
      });
    } catch (error) {
      const message = `Build API failed: ${
        error instanceof Error ? error.message : String(error)
      }`;
      setState(prev => ({
        ...prev,
        loadingBuilds: false,
        error: appendError(prev.error, message),
      }));
    }
  }, [componentId, fetchApi]);

  const loadBuildLogs = React.useCallback(
    async (buildId: string) => {
      if (!componentId) return;
      const normalizedBuildId = String(buildId || '').trim();
      if (!normalizedBuildId) {
        setState(prev => ({ ...prev, buildLogs: [], buildLogsMeta: {} }));
        return;
      }

      const buildLogsCacheKey = `${componentId}:${normalizedBuildId}:limit-${BUILD_LOG_DEFAULT_LIMIT}`;
      const cached = buildLogsCacheRef.current.get(buildLogsCacheKey);
      if (cached) {
        const logs = Array.isArray(cached?.logs) ? cached.logs : [];
        setState(prev => ({
          ...prev,
          selectedBuildId: normalizedBuildId,
          buildLogs: logs,
          buildLogsMeta: cached?.resolved ?? {},
        }));
      }

      setState(prev => ({ ...prev, loadingBuildLogs: true }));
      try {
        const payload = await measureAsync(
          'ui.buildLogs.fetch',
          async () => {
            const params = new URLSearchParams();
            params.set('buildId', normalizedBuildId);
            params.set('limit', String(BUILD_LOG_DEFAULT_LIMIT));
            const path = `/api/build-logs/logs/by-id/${encodeURIComponent(
              componentId,
            )}?${params.toString()}`;
            return await fetchJson<BuildLogsResponse>(fetchApi, path);
          },
          { componentId, buildId: normalizedBuildId },
        );
        const logs = Array.isArray(payload?.logs) ? payload.logs : [];
        buildLogsCacheRef.current.set(buildLogsCacheKey, payload);

        setState(prev => ({
          ...prev,
          loadingBuildLogs: false,
          selectedBuildId: normalizedBuildId,
          buildLogs: logs,
          buildLogsMeta: payload?.resolved ?? {},
          lastUpdatedAt: Date.now(),
        }));
      } catch (error) {
        const message = `Build logs API failed: ${
          error instanceof Error ? error.message : String(error)
        }`;
        setState(prev => ({
          ...prev,
          loadingBuildLogs: false,
          error: appendError(prev.error, message),
        }));
      }
    },
    [componentId, fetchApi],
  );

  const refresh = React.useCallback(async () => {
    if (!componentId) return;
    trackUxSignal('ui.logs.refresh.requested', { componentId, mode: 'build' });
    buildListCacheRef.current.clear();
    buildLogsCacheRef.current.clear();
    setState(prev => ({ ...prev, error: '' }));
    await loadBuildList();
  }, [componentId, loadBuildList]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!componentId) {
    return (
      <EmptyState
        title="Component ID Missing"
        missing="info"
        description="This entity does not have metadata.uid, so platform logs cannot be loaded."
      />
    );
  }

  let buildLogsContent: React.ReactNode = null;
  if (!state.selectedBuildId) {
    buildLogsContent = (
      <Typography variant="body2" color="textSecondary">
        (no builds available)
      </Typography>
    );
  } else if (state.buildLogs.length === 0) {
    buildLogsContent = (
      <Typography variant="body2" color="textSecondary">
        (no build logs found)
      </Typography>
    );
  } else {
    buildLogsContent = (
      <LogViewer
        sourceLabel="Build Step"
        emptyLabel="(no build logs found)"
        rows={state.buildLogs.map((log, index) => ({
          key: `${log.timestamp || 'ts'}-${index}`,
          timestamp: formatTimestamp(log.timestamp),
          severity: log.severity || '-',
          source: log.buildStep || '-',
          message: extractLogMessage(log) || '-',
        }))}
      />
    );
  }

  const refreshAction = (
    <Box display="flex" alignItems="center" gridGap={8}>
      <Typography variant="caption" color="textSecondary">
        Updated {relativeLastUpdated(state.lastUpdatedAt)}
      </Typography>
      <Button onClick={() => void refresh()}>Refresh</Button>
    </Box>
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <InfoCard title="Latest Builds" action={refreshAction}>
          {state.loadingBuilds ? <Progress /> : null}
          {state.error ? (
            <Box mb={2}>
              <Typography
                color="error"
                variant="body2"
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {state.error}
              </Typography>
            </Box>
          ) : null}
          {state.builds.length === 0 && !state.loadingBuilds ? (
            <Typography variant="body2" color="textSecondary">
              (no builds found)
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Build ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Branch</TableCell>
                    <TableCell>Commit</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>Finish</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.builds.map(build => {
                    const buildId = String(build.id || '');
                    const selected =
                      Boolean(buildId) && buildId === state.selectedBuildId;
                    return (
                      <TableRow
                        key={
                          buildId || `${build.startTime}-${build.finishTime}`
                        }
                        hover
                        selected={selected}
                        onClick={() => {
                          if (!buildId) return;
                          trackUxSignal('ui.build.selected', {
                            componentId,
                            buildId,
                          });
                          void loadBuildLogs(buildId);
                        }}
                        style={{ cursor: buildId ? 'pointer' : 'default' }}
                        tabIndex={buildId ? 0 : undefined}
                        onKeyDown={event => {
                          if (!buildId) return;
                          if (event.key !== 'Enter' && event.key !== ' ')
                            return;
                          event.preventDefault();
                          trackUxSignal('ui.build.selected.keyboard', {
                            componentId,
                            buildId,
                          });
                          void loadBuildLogs(buildId);
                        }}
                        aria-label={
                          buildId ? `Open build logs ${buildId}` : undefined
                        }
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            style={{
                              fontFamily: 'Menlo, Monaco, Consolas, monospace',
                              fontSize: 12,
                            }}
                          >
                            {buildId || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip value={build.status || '-'} />
                        </TableCell>
                        <TableCell>{build.branchName || '-'}</TableCell>
                        <TableCell>{build.shortSha || '-'}</TableCell>
                        <TableCell>
                          {formatTimestamp(build.startTime)}
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(build.finishTime)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfoCard>
      </Grid>

      <Grid item xs={12}>
        <Accordion
          expanded={logsExpanded}
          onChange={(_e, isExpanded) => {
            setLogsExpanded(isExpanded);
            if (isExpanded && state.selectedBuildId) {
              void loadBuildLogs(state.selectedBuildId);
            }
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              mr={1}
            >
              <Typography style={{ fontWeight: 700, fontSize: 16 }}>
                Build Logs
              </Typography>
              {state.builds.length > 0 ? (
                <Select
                  value={state.selectedBuildId}
                  onChange={e => {
                    e.stopPropagation();
                    const bid = String(e.target.value);
                    trackUxSignal('ui.build.selected.dropdown', {
                      componentId,
                      buildId: bid,
                    });
                    setState(prev => ({
                      ...prev,
                      selectedBuildId: bid,
                      buildLogs: [],
                      buildLogsMeta: {},
                    }));
                    void loadBuildLogs(bid);
                  }}
                  onClick={e => e.stopPropagation()}
                  variant="outlined"
                  margin="dense"
                  style={{ minWidth: 240, fontSize: 12 }}
                >
                  {state.builds.map(build => (
                    <MenuItem
                      key={build.id}
                      value={build.id}
                      style={{ fontSize: 12 }}
                    >
                      {String(build.id || '').slice(0, 8)}…{' '}
                      {build.branchName || ''} ({build.status || '–'})
                    </MenuItem>
                  ))}
                </Select>
              ) : null}
            </Box>
          </AccordionSummary>
          <AccordionDetails style={{ flexDirection: 'column' }}>
            {state.loadingBuildLogs ? <Progress /> : null}
            {buildLogsContent}
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
};
